import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {ObserveType, ProfileMode, REQUEST_OPTIONS, ResponseType} from '../../app.constants';
import {ApiService} from '../api';
import {FileModel, mapToFileModel} from '../utils/files';
import {Headers} from '../utils/headers';

import {FileDescription, FileDescriptionsWithTotal, FileStats, SignatureData, SignatureInfo} from './files.model';

@Injectable()
export class FileService {

  constructor(private api: ApiService,
              private translateService: TranslateService) {
  }

  public getFile(fileId: number, silent = false, withWaterMarkText?: boolean, profileMode: ProfileMode = ProfileMode.CLASSIC): Observable<FileModel> {
    const api = silent ? this.api.silent : this.api;
    let url = `/file/api/v1/file-description/${fileId}?profile-mode=${profileMode}`;
    if (withWaterMarkText) {
      url += `&watermark-text=${(this.translateService.instant('COMMON.payslip_watermark_text'))}`;
    }
    return api.get(url, {
      requestOptions: {
        observe: ObserveType.RESPONSE,
        responseType: ResponseType.BLOB
      }
    }).pipe(
      map(mapToFileModel())
    );
  }

  public getFilesAsZip(uuid: string): Observable<FileModel> {
    const url = `/file/api/v1/file-description/download?page=0&per-page=1000&tags=uuid==${uuid}`;
    return this.api.silent.get(url, {
      requestOptions: {
        observe: ObserveType.RESPONSE,
        responseType: ResponseType.BLOB
      }
    }).pipe(
      map(mapToFileModel())
    );
  }

  public getFiles(search: string, profileMode: ProfileMode = ProfileMode.CLASSIC): Observable<FileDescription[]> {
    return this.api.get(`/file/api/v1/file-description?profile-mode=${profileMode}&${search}`);
  }

  public fetchFiles(searchAndSort = '', perPage = 10, page = 0, silent = false, profileMode: ProfileMode = ProfileMode.CLASSIC): Observable<FileDescriptionsWithTotal> {
    const api = silent ? this.api.silent : this.api;
    return api.get(`/file/api/v1/file-description/?profile-mode=${profileMode}&${searchAndSort}&per-page=${perPage}&page=${page}`,
      REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map(result => ({
          data: result.status === 204 ? [] : result.body,
          total: parseInt(Headers.get(result.headers, 'X-Total-Count'), 10)
        }))
      );
  }

  public fetchFilesByContractId(contractId: number, parameters: string, silent = false, profileMode: ProfileMode = ProfileMode.CLASSIC)
    : Observable<FileDescriptionsWithTotal> {
    const api = silent ? this.api.silent : this.api;
    return api.get(`/file/api/v1/contract/${contractId}?profile-mode=${profileMode}&${parameters}`,
      REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map(result => ({
          data: result.status === 204 ? [] : result.body,
          total: parseInt(Headers.get(result.headers, 'X-Total-Count'), 10),
          employeeDocumentCount: parseInt(Headers.get(result.headers, 'X-Employee-Document-Count'), 10)
        }))
      );
  }

  public fetchMyFilesByContractId(contractId: number, parameters: string, silent = false, profileMode: ProfileMode = ProfileMode.CLASSIC)
    : Observable<FileDescriptionsWithTotal> {
    const api = silent ? this.api.silent : this.api;
    return api.get(`/file/api/v1/my-contract/${contractId}?profile-mode=${profileMode}&${parameters}`,
      REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map(result => ({
          data: result.status === 204 ? [] : result.body,
          total: parseInt(Headers.get(result.headers, 'X-Total-Count'), 10),
          employeeDocumentCount: parseInt(Headers.get(result.headers, 'X-Employee-Document-Count'), 10)
        }))
      );
  }

  public deleteFile(fileId: number, profileMode: ProfileMode = ProfileMode.CLASSIC) {
    return this.api.delete(`/file/api/v1/file-description/${fileId}?profile-mode=${profileMode}`);
  }

  public deleteFiles(fileIds: number[], profileMode: ProfileMode = ProfileMode.CLASSIC) {
    return this.api.delete(
      `/file/api/v1/file-description?profile-mode=${profileMode}&${fileIds.map((id: number) => `fileIds=${id}`).join('&')}`);
  }

  public addFile(formData: FormData, options: any, silent: boolean, profileMode: ProfileMode = ProfileMode.CLASSIC) {
    const api = silent ? this.api.silent : this.api;
    return api.post(`/file/api/v1/file-description?profile-mode=${profileMode}`, formData, options);
  }

  public sendFiles(fileIds: number[]) {
    return this.api.post(
      `/file/api/v1/file-description/send?${fileIds.map((id: number) => `fileIds=${id}`).join('&')}`);
  }

  public initiateSignature(fileId: number, signatureData: SignatureData): Observable<SignatureData> {
    return this.api.post(`/file/api/v1/signature/${fileId}`, signatureData);
  }

  public defineSignatureFields(fileId: number, fields: SignatureInfo[]): Observable<FileDescription> {
    return this.api.post(`/file/api/v1/signature/${fileId}/placeholders`, {
      fields: fields.map(({markerName, signingFieldPlacement}) => ({markerName, signingFieldPlacement}))
    });
  }

  public checkSignature(fileId: number): Observable<FileDescription[]> {
    return this.api.post(`/file/api/v1/signature/${fileId}/check`, {});
  }

  public uploadFile(file: File, blob: Blob) {
    return this.api.uploadFile(`/file/api/v1/report`, file, {
      requestOptions: REQUEST_OPTIONS.RESPONSE_TYPE.TEXT.requestOptions,
      fileName: file.name,
      additionalData: {
        body: blob
      }
    });
  }

  public getFilesStats(contractId: number, type: string, family = '', silent = false): Observable<FileStats> {
    const familyQuery = family?.length ? `&family=${family}` : '';
    const api = silent ? this.api.silent : this.api;

    return api.get(`/file/api/v1/contract/${contractId}/stats?type=${type}${familyQuery}`);

  }

  public getFileByType(fileDescriptionId: number, type: ResponseType, profileMode: ProfileMode = ProfileMode.CLASSIC) {
    return this.api.get(`/file/api/v1/file-description/${fileDescriptionId}?profile-mode=${profileMode}`, {
      requestOptions: {
        observe: ObserveType.RESPONSE,
        responseType: type
      }
    }).pipe(
      map((response: any) => {
        return response.body;
      })
    );
  }


}
